import { useState, useEffect, useRef } from 'react';
import { Star, Quote, ChevronLeft, ChevronRight, Plus, Camera, X, Send, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate initials avatar
const getInitials = (name) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Get gradient color based on name
const getAvatarGradient = (name) => {
  const gradients = [
    'from-[#3B82F6] to-[#8B5CF6]',
    'from-[#8B5CF6] to-[#EC4899]',
    'from-[#F97316] to-[#EAB308]',
    'from-[#10B981] to-[#3B82F6]',
    'from-[#EC4899] to-[#F97316]'
  ];
  const index = name.charCodeAt(0) % gradients.length;
  return gradients[index];
};

const Testimonials = () => {
  const { user } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    content: '',
    project_type: 'website'
  });
  const autoPlayRef = useRef(null);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  useEffect(() => {
    // Auto-advance testimonials
    if (testimonials.length > 1) {
      autoPlayRef.current = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % testimonials.length);
      }, 6000);
    }
    
    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [testimonials.length]);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials?limit=10`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to leave a review');
      return;
    }

    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(`${API}/testimonials`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Thank you! Your review has been submitted for approval.');
      setShowForm(false);
      setFormData({ rating: 5, title: '', content: '', project_type: 'website' });
    } catch (error) {
      console.error('Error submitting testimonial:', error);
      toast.error('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextTestimonial = () => {
    setCurrentIndex(prev => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const StarRating = ({ rating, interactive = false, onChange }) => (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type={interactive ? 'button' : undefined}
          onClick={() => interactive && onChange && onChange(star)}
          className={`transition-all ${interactive ? 'cursor-pointer hover:scale-110' : ''}`}
          disabled={!interactive}
        >
          <Star
            size={interactive ? 28 : 18}
            className={`transition-colors ${
              star <= rating 
                ? 'text-[#F97316] fill-[#F97316]' 
                : 'text-[var(--text-muted)]'
            }`}
          />
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <section className="py-24" style={{ background: 'var(--bg-card)' }}>
        <div className="flex justify-center">
          <div className="w-10 h-10 border-4 border-[#3B82F6]/30 border-t-[#3B82F6] rounded-full animate-spin"></div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative py-16 md:py-20 overflow-hidden" style={{ background: 'var(--bg-card)' }}>
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#F97316]/50 to-transparent"></div>
      <div className="absolute top-1/2 left-1/4 w-[400px] h-[400px] bg-[#F97316]/5 rounded-full blur-[150px]"></div>
      <div className="absolute top-1/2 right-1/4 w-[400px] h-[400px] bg-[#8B5CF6]/5 rounded-full blur-[150px]"></div>

      <div className="relative max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F97316]/10 border border-[#F97316]/30 rounded-full mb-6">
              <Star size={16} className="text-[#F97316] fill-[#F97316]" />
              <span className="text-[#F97316] text-sm font-semibold">Client Reviews</span>
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-[var(--text-primary)] mb-4 leading-tight">
              What Our{' '}
              <span className="bg-gradient-to-r from-[#F97316] via-[#EC4899] to-[#8B5CF6] bg-clip-text text-transparent">
                Clients Say
              </span>
            </h2>
            
            <p className="text-[var(--text-muted)] max-w-xl">
              Real stories from businesses we've helped transform with our digital solutions
            </p>
          </div>

          {/* Add Review Button */}
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-6 py-3 font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl hover:shadow-[#F97316]/25"
            style={{ background: 'linear-gradient(135deg, #F97316, #EC4899)' }}
          >
            <Plus size={20} />
            Share Your Experience
          </button>
        </div>

        {/* Testimonials Carousel */}
        {testimonials.length > 0 ? (
          <div className="relative">
            {/* Main Testimonial Card */}
            <div className="bg-[var(--bg-dark)] border border-[var(--border)] rounded-3xl p-8 md:p-12 relative overflow-hidden">
              {/* Quote Icon */}
              <div className="absolute top-8 right-8 opacity-10">
                <Quote size={120} className="text-[#F97316]" />
              </div>

              <div className="relative grid md:grid-cols-3 gap-8 items-center">
                {/* Author Info */}
                <div className="text-center md:text-left">
                  {/* Avatar */}
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-r ${getAvatarGradient(testimonials[currentIndex]?.user_name || 'User')} flex items-center justify-center mx-auto md:mx-0 mb-4 shadow-xl`}>
                    {testimonials[currentIndex]?.user_photo ? (
                      <img 
                        src={testimonials[currentIndex].user_photo} 
                        alt={testimonials[currentIndex].user_name}
                        className="w-full h-full rounded-2xl object-cover"
                      />
                    ) : (
                      <span className="text-white text-2xl font-bold">
                        {getInitials(testimonials[currentIndex]?.user_name || 'User')}
                      </span>
                    )}
                  </div>

                  <h4 className="text-xl font-bold text-[var(--text-primary)] mb-1">
                    {testimonials[currentIndex]?.user_name}
                  </h4>
                  {testimonials[currentIndex]?.company && (
                    <p className="text-[#8B5CF6] font-medium text-sm mb-3">
                      {testimonials[currentIndex].company}
                    </p>
                  )}
                  
                  <StarRating rating={testimonials[currentIndex]?.rating || 5} />
                  
                  {testimonials[currentIndex]?.project_type && (
                    <span className="inline-block mt-4 px-3 py-1 bg-[#3B82F6]/10 border border-[#3B82F6]/30 rounded-full text-[#3B82F6] text-xs font-semibold capitalize">
                      {testimonials[currentIndex].project_type}
                    </span>
                  )}
                </div>

                {/* Testimonial Content */}
                <div className="md:col-span-2">
                  <h3 className="text-2xl md:text-3xl font-bold text-[var(--text-primary)] mb-6">
                    "{testimonials[currentIndex]?.title}"
                  </h3>
                  <p className="text-[var(--text-secondary)] text-lg leading-relaxed">
                    {testimonials[currentIndex]?.content}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            {testimonials.length > 1 && (
              <>
                <button
                  onClick={prevTestimonial}
                  className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-6 w-12 h-12 bg-[var(--bg-card)] border border-[var(--border)] rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[#F97316]/50 transition-all shadow-lg hover:scale-110"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={nextTestimonial}
                  className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-6 w-12 h-12 bg-[var(--bg-card)] border border-[var(--border)] rounded-full flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[#F97316]/50 transition-all shadow-lg hover:scale-110"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {/* Dots */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex 
                      ? 'w-8 h-2 bg-gradient-to-r from-[#F97316] to-[#EC4899]' 
                      : 'w-2 h-2 bg-[var(--text-muted)]/30 hover:bg-[var(--text-muted)]/50'
                  }`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Quote size={48} className="text-[var(--text-muted)] mx-auto mb-4" />
            <p className="text-[var(--text-muted)]">No reviews yet. Be the first to share your experience!</p>
          </div>
        )}

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-6 mt-10">
          {[
            { value: '4.9', label: 'Average Rating', icon: Star },
            { value: '200+', label: 'Happy Clients', icon: Quote },
            { value: '99%', label: 'Satisfaction', icon: Star }
          ].map((stat, i) => (
            <div key={i} className="text-center p-6 bg-[var(--bg-dark)]/50 rounded-2xl border border-[var(--border)]">
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#F97316] to-[#EC4899] bg-clip-text text-transparent mb-2">
                {stat.value}
              </div>
              <div className="text-[var(--text-muted)] text-sm">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Add Review Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div 
            className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="p-6 border-b border-[var(--border)] flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-[var(--text-primary)]">Share Your Experience</h3>
                <p className="text-[var(--text-muted)] text-sm">Help others by sharing your feedback</p>
              </div>
              <button
                onClick={() => setShowForm(false)}
                className="p-2 text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-elevated)] rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {!user && (
                <div className="bg-[#F97316]/10 border border-[#F97316]/30 rounded-xl p-4 text-center">
                  <p className="text-[#F97316] text-sm">Please login to submit a review</p>
                </div>
              )}

              {/* Rating */}
              <div>
                <label className="block text-[var(--text-secondary)] mb-3 font-medium">
                  Your Rating *
                </label>
                <StarRating 
                  rating={formData.rating} 
                  interactive={true}
                  onChange={(rating) => setFormData({ ...formData, rating })}
                />
              </div>

              {/* Project Type */}
              <div>
                <label className="block text-[var(--text-secondary)] mb-3 font-medium">
                  Project Type
                </label>
                <div className="flex flex-wrap gap-2">
                  {['website', 'app', 'design', 'ai', 'business tools'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setFormData({ ...formData, project_type: type })}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${
                        formData.project_type === type
                          ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white'
                          : 'bg-[var(--bg-elevated)] text-[var(--text-muted)] hover:text-[var(--text-primary)] border border-[var(--border)]'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>

              {/* Title */}
              <div>
                <label className="block text-[var(--text-secondary)] mb-2 font-medium">
                  Review Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Summarize your experience"
                  className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316]/50 transition-all"
                  required
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-[var(--text-secondary)] mb-2 font-medium">
                  Your Review *
                </label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Tell us about your experience working with CLOUD IT..."
                  rows={4}
                  className="w-full bg-[var(--bg-dark)] border border-[var(--border)] rounded-xl px-4 py-3 text-[var(--text-primary)] focus:outline-none focus:border-[#F97316] focus:ring-1 focus:ring-[#F97316]/50 transition-all resize-none"
                  required
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting || !user}
                className="w-full py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-[1.02] hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                style={{ background: 'linear-gradient(135deg, #F97316, #EC4899)' }}
              >
                {submitting ? (
                  <>
                    <Loader2 size={20} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Submit Review
                  </>
                )}
              </button>

              <p className="text-[var(--text-muted)] text-xs text-center">
                Your review will be published after approval
              </p>
            </form>
          </div>
        </div>
      )}
    </section>
  );
};

export default Testimonials;
